import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/utils/queryKeys';
import { normalizeError } from '@/lib/error-types';

/** Stale time for role queries — roles change extremely rarely */
const ROLE_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

type AppRole = 'admin' | 'course_admin' | 'super_admin';

interface UseUserRoleReturn {
  /** Raw array of roles assigned to the current user */
  roles: AppRole[];
  /** True if user has 'admin' or 'super_admin' */
  isAdmin: boolean;
  /** True if user has 'course_admin' or 'super_admin' */
  isCourseAdmin: boolean;
  /** True if user has 'super_admin' */
  isSuperAdmin: boolean;
  /** True while the roles query is loading */
  isLoading: boolean;
}

/** Exported for reuse in MaintenanceGate (fail-open role check). */
export async function fetchMyRoles(): Promise<AppRole[]> {
  const { data, error } = await supabase.rpc('get_my_roles');
  if (error) {
    throw normalizeError(error);
  }
  // RPC returns app_role[] — Supabase serializes as string[]
  return (data as AppRole[]) ?? [];
}

/**
 * Hook to access the current user's RBAC roles.
 *
 * Returns memoized boolean flags for convenient permission checks.
 * Only fetches when the user is authenticated.
 */
export function useUserRole(): UseUserRoleReturn {
  const { user } = useAuth();
  const userId = user?.id;

  const { data: roles = [], isPending, isFetching } = useQuery({
    queryKey: queryKeys.user.roles(userId ?? ''),
    queryFn: fetchMyRoles,
    enabled: !!userId,
    staleTime: ROLE_STALE_TIME_MS,
    throwOnError: true,
  });

  // v5: `isLoading = isPending && isFetching`. When `enabled` just
  // flipped true, isFetching can still be false for a tick → isLoading
  // false → default roles=[] used → RoleGuard redirects before the
  // first fetch even kicks off. Gate on `isPending` (no data yet) OR
  // `isFetching` (refetch in flight) so RoleGuard shows skeleton until
  // we actually have an answer.
  const isLoading = isPending || isFetching;

  const flags = useMemo(() => {
    const roleSet = new Set<string>(roles);
    return {
      isAdmin: roleSet.has('admin') || roleSet.has('super_admin'),
      isCourseAdmin: roleSet.has('course_admin') || roleSet.has('super_admin'),
      isSuperAdmin: roleSet.has('super_admin'),
    };
  }, [roles]);

  return {
    roles,
    ...flags,
    isLoading,
  };
}
